import { useEffect, useRef } from "react"
import type { ListingRun } from "@nova-ai/shared"
import { ChevronDown } from "lucide-react"

import { formatDateTime } from "../../lib/format-date"
import { RUN_STATUS_LABELS } from "./run-status-label"

/** Quiet run context beside the workspace tabs; detailed provenance stays one click away. */
export function RunMetadata({ run }: { run: ListingRun }) {
  const detailsRef = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    const dismissOutside = (event: Event) => {
      const details = detailsRef.current
      if (details?.open && event.target instanceof Node && !details.contains(event.target)) {
        details.open = false
      }
    }
    const dismissWithEscape = (event: KeyboardEvent) => {
      const details = detailsRef.current
      if (
        event.key === "Escape" &&
        details?.open &&
        event.target instanceof Node &&
        details.contains(event.target)
      ) {
        details.open = false
        details.querySelector("summary")?.focus()
      }
    }
    document.addEventListener("pointerdown", dismissOutside)
    document.addEventListener("focusin", dismissOutside)
    document.addEventListener("keydown", dismissWithEscape)
    return () => {
      document.removeEventListener("pointerdown", dismissOutside)
      document.removeEventListener("focusin", dismissOutside)
      document.removeEventListener("keydown", dismissWithEscape)
    }
  }, [])
  return (
    <div className="ml-auto flex min-w-0 items-center gap-3 text-xs text-muted-foreground">
      <span className="hidden truncate font-mono md:block" title={run.slug}>
        {run.slug}
      </span>

      {run.simpleCount !== null && (
        <span className="hidden whitespace-nowrap tabular-nums sm:inline">
          {`${run.simpleCount.toLocaleString("en-US")} saved variants`}
        </span>
      )}

      <details ref={detailsRef} className="group/run-details relative shrink-0">
        <summary
          aria-label="Run details"
          className="flex min-h-8 cursor-pointer list-none items-center gap-1 rounded-full px-1.5 text-xs sm:px-2 text-muted-foreground outline-none hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden"
        >
          <span className="hidden sm:inline">Run details</span>

          <span className="sm:hidden">Details</span>

          <ChevronDown
            aria-hidden
            className="size-3 transition-transform group-open/run-details:rotate-180"
          />
        </summary>

        <dl className="absolute right-0 z-40 mt-1 grid w-[min(22rem,calc(100vw-2rem))] grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-3 rounded-xl border border-border bg-popover p-4 text-left text-xs text-muted-foreground shadow-floating">
          <dt>Run</dt>

          <dd className="break-all font-mono text-foreground">{run.slug}</dd>

          {run.simpleCount !== null && (
            <>
              <dt>Variants at last validation</dt>

              <dd className="text-foreground tabular-nums">
                {run.simpleCount.toLocaleString("en-US")}
              </dd>
            </>
          )}

          <dt>Status</dt>

          <dd className="text-foreground">{RUN_STATUS_LABELS[run.status]}</dd>

          <dt>Started by</dt>

          <dd className="break-all text-foreground">{run.startedBy.replace(/^[a-z0-9]+:/i, "")}</dd>

          <dt>Created</dt>

          <dd className="text-foreground">
            <time dateTime={run.createdAt} title={run.createdAt}>
              {formatDateTime(run.createdAt)}
            </time>
          </dd>

          <dt>Updated</dt>

          <dd className="text-foreground">
            <time dateTime={run.updatedAt} title={run.updatedAt}>
              {formatDateTime(run.updatedAt)}
            </time>
          </dd>
        </dl>
      </details>
    </div>
  )
}
